import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from '@/lib/i18n';
import { Button, FormControl, FormField, FormItem, FormMessage, Input } from '@/components/ui';
import type { ProductFormValues } from '../schemas/productSchema';
import {
  buildComboPreviewPairs,
  comboKeyOf,
  type ComboLabelPair,
} from '../lib/variantPartition';

/**
 * §4 단일 SKU 테이블 (방향 A) — 평면 variant·조합 variant·등록 미리보기를 **동일 컬럼**으로
 * 한 테이블에 렌더한다. 종전 4분할(평면 리스트 / OptionGroupsEditor / ComboVariantList /
 * ComboPreviewCreate)의 "재고 채널"이 동일 구조였던 점을 살려 시각을 일원화한다.
 *
 * ★불변(회귀 0): 폼 바인딩(`variants.{index}.*`, `comboStocks[comboKey]`)·조합 키(comboKeyOf)·
 * 데카르트 곱 순서·재고추적 OFF disable·무게 폴백 모두 종전과 byte-동일. 이 컴포넌트는 **렌더 전용**
 * 이며 detailMapper 페이로드(평면 전체 / 조합 stock-only patch / comboStocks 등록)는 손대지 않는다.
 *
 * - 평면 행: 라벨/코드/추가금 편집 + 재고 채널 편집 + 행 삭제.
 * - 조합 행: 라벨(그룹쌍)·코드·추가금 **읽기 전용**(그룹 편집기 소유), 재고 채널만 편집.
 * - 등록 미리보기 행: 데카르트 곱 결과를 인라인 표시 + comboStocks 초기/안전 재고 입력(등록 전용).
 */

export interface SkuFlatEntry {
  /** useFieldArray field.id — React key. */
  fieldId: string;
  /** variants 폼 배열의 현재 인덱스 — FormField name 경로. */
  index: number;
}

/** 조합 variant 행 — 구조(라벨/코드/추가금)는 로드 스냅샷, 재고 채널은 variants.{index}.* 바인딩. */
export interface ComboVariantEntry {
  /** useFieldArray field.id — React key (인덱스 변동에도 입력 상태 보존). */
  fieldId: string;
  /** variants 폼 배열의 현재 인덱스 — FormField name 경로. */
  index: number;
  label: string;
  /**
   * 그룹-한정 라벨 쌍("색상: 차콜 · 사이즈: S") — variantCombinations 링크 해석 결과.
   * null = 해석 불가(레거시/매핑 누락) → 원본 label 폴백 표기.
   */
  labelPairs: ReadonlyArray<ComboLabelPair> | null;
  variantCode: string | null;
  additionalAmount: number | null;
}

export interface SkuTableProps {
  form: UseFormReturn<ProductFormValues>;
  /** 평면(비조합) variant 행 — 인덱스 보존. */
  flatEntries: ReadonlyArray<SkuFlatEntry>;
  /** 조합 variant 행 — 구조 읽기 전용 + 재고 편집(수정 모드 reconcile 대상). */
  comboEntries: ReadonlyArray<ComboVariantEntry>;
  /** 평면 행 삭제(useFieldArray.remove). */
  onRemoveFlat: (index: number) => void;
  /**
   * G1 — 수정 모드면 재고 라벨이 "현재고"(라이브 stock_qty). 등록 모드는 "초기 재고".
   * 또한 등록 모드에서만 데카르트 곱 미리보기 행(comboStocks 입력)을 렌더한다.
   */
  isEditMode: boolean;
}

/**
 * 헤더/행 공통 그리드(DESIGN-§4-unify §3.2) — 세 행 변형(평면/조합/미리보기)이 **동일 grid-template**
 * 을 공유해 한 테이블로 정렬된다. 1열(조합/라벨) 최대폭(min 180px), 마지막 auto=평면 삭제 버튼 열.
 * 컬럼: 조합/라벨 · 코드 · 추가금 · 현재고 · 안전재고 · 무게 · 재고추적 · (액션).
 */
// 열: 옵션 라벨 · 옵션 코드(variantCode) · 자체 옵션코드(optionCode) · 추가 금액 · 재고 · 안전 재고 · 무게 · 재고추적 · 삭제
const SKU_ROW_GRID =
  'minmax(180px,1.6fr) minmax(0,0.9fr) minmax(0,0.9fr) minmax(0,0.8fr) minmax(0,0.9fr) minmax(0,0.9fr) minmax(0,0.8fr) minmax(120px,1fr) 104px';

/** 자체 옵션코드(SKU 식별자, be-catalog V126 option_code) 최대 길이. */
const OPTION_CODE_MAX = 64;

const HEADER_CLASS = 'text-[12px] font-medium';
const READONLY_VALUE_CLASS = 'text-[13px] tabular-nums';

/** 조합/미리보기 행의 읽기전용 구조 셀 — surface-1 옅은 음영(입력 셀 surface-0 대비, §3.5). */
const READONLY_CELL_CLASS = 'flex min-h-[28px] items-center rounded-sm px-2 py-1';
const READONLY_CELL_STYLE = { background: 'var(--surface-1)' } as const;

/**
 * 헤더 셀 정렬 — 모든 열 왼쪽(QA-316). 아래 입력칸·체크박스가 모두 왼쪽부터 채워져, 종전의 수치열 우정렬·
 * 재고추적 중앙 헤더는 값과 어긋나 보였다. 액션열은 헤더 비움.
 */
const HEADER_ALIGN_CLASS = 'text-left';

/** 조합/미리보기 라벨 쌍("색상: 차콜 · 사이즈: S") 표기. 쌍이 없으면 fallback 텍스트. */
const ComboLabelPairs = ({
  pairs,
  fallback,
  testId,
}: {
  pairs: ReadonlyArray<ComboLabelPair> | null;
  fallback: string;
  testId?: string;
}) => {
  if (!pairs || pairs.length === 0) {
    return (
      <span className="text-[14px] font-medium" style={{ color: 'var(--content-strong)' }}>
        {fallback}
      </span>
    );
  }
  return (
    <span
      className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-[14px]"
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {pairs.map((pair, pairIdx) => (
        <span key={`${pair.groupName}-${pairIdx}`} className="whitespace-nowrap">
          {pairIdx > 0 ? (
            <span aria-hidden="true" style={{ color: 'var(--content-muted)' }}>
              {' · '}
            </span>
          ) : null}
          <span style={{ color: 'var(--content-muted)' }}>{pair.groupName}: </span>
          <span className="font-medium" style={{ color: 'var(--content-strong)' }}>
            {pair.choiceLabel}
          </span>
        </span>
      ))}
    </span>
  );
};

export const SkuTable = ({
  form,
  flatEntries,
  comboEntries,
  onRemoveFlat,
  isEditMode,
}: SkuTableProps) => {
  const { t } = useTranslation('products');

  // G1 — 수정 모드는 "현재고", 등록 모드는 "초기 재고".
  const stockLabel = isEditMode
    ? t('products.fields.currentStockQty', '현재고')
    : t('products.fields.initialStockQty', '초기 재고');

  // 등록 미리보기(데카르트 곱) — 등록 모드에서만. comboStocks 입력 바인딩(기존 ComboPreviewCreate 규칙).
  const groups = form.watch('optionGroups') ?? [];
  const previewCombos = useMemo(
    () => (isEditMode ? [] : buildComboPreviewPairs(groups)),
    [isEditMode, groups],
  );
  const comboStocks = form.watch('comboStocks') ?? {};

  const setComboStock = (
    key: string,
    channel: 'initialStockQty' | 'safetyStockQty',
    raw: number,
  ) => {
    const next = Number.isFinite(raw) ? raw : null;
    const current = form.getValues('comboStocks') ?? {};
    const entry = { ...(current[key] ?? {}), [channel]: next };
    form.setValue('comboStocks', { ...current, [key]: entry }, { shouldDirty: true });
  };

  const setComboOptionCode = (key: string, value: string) => {
    const current = form.getValues('comboStocks') ?? {};
    const entry = { ...(current[key] ?? {}), optionCode: value };
    form.setValue('comboStocks', { ...current, [key]: entry }, { shouldDirty: true });
  };

  const hasAnyRow =
    flatEntries.length > 0 || comboEntries.length > 0 || previewCombos.length > 0;
  if (!hasAnyRow) return null;

  const columnHeaders: ReadonlyArray<string> = [
    t('products.fields.variantLabel', '옵션 라벨'),
    t('products.fields.variantCode', '옵션 코드'),
    t('products.fields.optionCode', '자체 옵션코드'),
    t('products.fields.additionalAmount', '추가 금액'),
    stockLabel,
    t('products.fields.safetyStockQty', '안전 재고'),
    t('products.fields.variantWeightGrams', '무게 (g)'),
    t('products.fields.stockTrackingEnabled', '재고추적'),
  ];

  return (
    <div className="flex flex-col gap-2" data-testid="sku-table">
      {/* 헤더 — md 이상에서만 그리드 헤더 노출(모바일은 행 안 미니 라벨로 대체, §3.6). */}
      <div
        className="hidden items-center gap-3 rounded-md px-4 py-2 md:grid"
        style={{
          gridTemplateColumns: SKU_ROW_GRID,
          background: 'var(--surface-1)',
          border: '1px solid transparent',
          borderBottom: '1px solid var(--divider-strong)',
        }}
        data-testid="sku-table-header"
      >
        {columnHeaders.map((label) => (
          <span
            key={label}
            className={`${HEADER_CLASS} ${HEADER_ALIGN_CLASS}`}
            style={{ color: 'var(--content-muted)' }}
          >
            {label}
          </span>
        ))}
        <span aria-hidden="true" />
      </div>

      <ul className="flex flex-col gap-2">
        {/* 평면 행 — 라벨/코드/추가금 + 재고 채널 편집 + 삭제. */}
        {flatEntries.map(({ fieldId, index: idx }) => {
          const trackingOff = form.watch(`variants.${idx}.stockTrackingEnabled`) === false;
          return (
            <li
              key={fieldId}
              className="grid grid-cols-1 items-start gap-3 rounded-md p-4 md:items-center"
              style={{
                gridTemplateColumns: SKU_ROW_GRID,
                background: 'var(--surface-0)',
                border: '1px solid var(--divider-strong)',
              }}
              data-testid="sku-flat-row"
            >
              <FormField
                control={form.control}
                name={`variants.${idx}.label`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...f}
                        size="sm"
                        // QA-521 — 옵션이 1개면 비워도 된다(저장 시 "기본").
                        required={flatEntries.length > 1}
                        aria-required={flatEntries.length > 1 ? 'true' : 'false'}
                        aria-label={`${t('products.fields.variantLabel', '옵션 라벨')} ${idx + 1}`}
                        placeholder={flatEntries.length > 1 ? '블랙 / S 등' : '비우면 "기본"'}
                      />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.variants?.[idx]?.label?.message
                        ? t(form.formState.errors.variants[idx]!.label!.message!, {
                            defaultValue: form.formState.errors.variants[idx]!.label!.message,
                          })
                        : null}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.variantCode`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...f}
                        size="sm"
                        value={f.value ?? ''}
                        maxLength={50}
                        placeholder="OPT-001"
                        aria-label={`${t('products.fields.variantCode', '옵션 코드')} ${idx + 1}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {/* 자체 옵션코드(고도몰 옵션 수정 팝업의 "자체 옵션 코드"). 비우면 지운다(편집 시 ""). */}
              <FormField
                control={form.control}
                name={`variants.${idx}.optionCode`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...f}
                        size="sm"
                        value={f.value ?? ''}
                        maxLength={OPTION_CODE_MAX}
                        placeholder={t('products.fields.optionCodePlaceholder', '예: A')}
                        aria-label={`${t('products.fields.optionCode', '자체 옵션코드')} ${idx + 1}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.additionalAmount`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        {...f}
                        value={f.value ?? 0}
                        onChange={(e) => f.onChange(e.target.valueAsNumber)}
                        aria-label={`${t('products.fields.additionalAmount', '추가 금액')} ${idx + 1}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.initialStockQty`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        {...f}
                        value={f.value ?? 0}
                        onChange={(e) => f.onChange(e.target.valueAsNumber)}
                        // TASK-2: 무한판매(재고추적 OFF) 옵션은 재고 입력 무의미 → 비활성.
                        disabled={trackingOff}
                        aria-label={`${stockLabel} ${idx + 1}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.safetyStockQty`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        placeholder={t('products.fields.safetyStockPlaceholder', '미설정')}
                        value={f.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          f.onChange(Number.isFinite(next) ? next : null);
                        }}
                        disabled={trackingOff}
                        aria-label={`${t('products.fields.safetyStockQty', '안전 재고')} ${idx + 1}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {/* 결정① — 옵션 실측 무게(g). 비우면 상품 무게(§7)로 폴백(해외배송비 실무게 산정). */}
              <FormField
                control={form.control}
                name={`variants.${idx}.weightGrams`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        placeholder={t('products.fields.variantWeightGramsPlaceholder', '상품 무게 사용')}
                        value={f.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          f.onChange(Number.isFinite(next) ? next : null);
                        }}
                        aria-label={`${t('products.fields.variantWeightGrams', '무게 (g)')} ${idx + 1}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {/* TASK-2: per-variant 재고추적 토글. OFF=무한판매(재고 미설정, 품절 안 됨). */}
              <FormField
                control={form.control}
                name={`variants.${idx}.stockTrackingEnabled`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <label
                        className="inline-flex items-center gap-2 text-[13px]"
                        style={{ color: 'var(--content-default)' }}
                      >
                        <input
                          type="checkbox"
                          checked={f.value ?? true}
                          onChange={(e) => f.onChange(e.target.checked)}
                          aria-label={`${t('products.fields.stockTrackingEnabled', '재고추적')} ${idx + 1}`}
                        />
                        {f.value ?? true
                          ? t('products.fields.stockTrackingEnabledOn', '사용 (품절 관리)')
                          : t('products.fields.stockTrackingEnabledOff', '무한판매 (품절 안 됨)')}
                      </label>
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex md:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveFlat(idx)}
                  aria-label={t('products.actions.removeVariantAria', {
                    index: idx + 1,
                    defaultValue: t('products.actions.removeVariant', '옵션 삭제'),
                  })}
                >
                  {t('products.actions.removeVariant', '옵션 삭제')}
                </Button>
              </div>
            </li>
          );
        })}

        {/* 조합 행 — 구조(라벨/코드/추가금) 읽기 전용, 재고 채널만 편집. */}
        {comboEntries.map((entry) => {
          const idx = entry.index;
          const trackingOff = form.watch(`variants.${idx}.stockTrackingEnabled`) === false;
          return (
            <li
              key={entry.fieldId}
              className="grid grid-cols-1 items-start gap-3 rounded-md p-4 md:items-center"
              style={{
                gridTemplateColumns: SKU_ROW_GRID,
                // §3.4 — 세 행 변형 배경 통일(surface-0). 읽기전용 단서는 구조 셀 음영으로(아래).
                background: 'var(--surface-0)',
                border: '1px solid var(--divider-strong)',
              }}
              data-testid="sku-combo-row"
            >
              {/* §3.5 — 구조 셀(조합/라벨·코드·추가금) 읽기전용 단서: surface-1 옅은 톤 + 우측 divider 로
                  "왼쪽=고정 구조 / 오른쪽=편집 재고" 구분(추가금 셀에 우측 분할선). */}
              <div className={READONLY_CELL_CLASS} style={READONLY_CELL_STYLE}>
                <ComboLabelPairs
                  pairs={entry.labelPairs}
                  fallback={entry.label}
                  testId="sku-combo-label-pairs"
                />
              </div>
              <div
                className={`${READONLY_CELL_CLASS} ${READONLY_VALUE_CLASS}`}
                style={{ ...READONLY_CELL_STYLE, color: 'var(--content-default)' }}
              >
                {entry.variantCode ?? '—'}
              </div>
              {/* 자체 옵션코드 — 조합 구조는 고정이지만 이 값은 variantCode 매칭 패치로 바꿀 수 있다. */}
              <FormField
                control={form.control}
                name={`variants.${idx}.optionCode`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...f}
                        size="sm"
                        value={f.value ?? ''}
                        maxLength={OPTION_CODE_MAX}
                        placeholder={t('products.fields.optionCodePlaceholder', '예: A')}
                        aria-label={`${entry.label} ${t('products.fields.optionCode', '자체 옵션코드')}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div
                className={`${READONLY_CELL_CLASS} ${READONLY_VALUE_CLASS} text-right`}
                style={{
                  ...READONLY_CELL_STYLE,
                  color: 'var(--content-default)',
                  borderRight: '1px solid var(--divider-soft)',
                }}
              >
                {(entry.additionalAmount ?? 0).toLocaleString('ko-KR')}
              </div>
              <FormField
                control={form.control}
                name={`variants.${idx}.initialStockQty`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        {...f}
                        value={f.value ?? 0}
                        onChange={(e) => f.onChange(e.target.valueAsNumber)}
                        disabled={trackingOff}
                        aria-label={`${entry.label} ${stockLabel}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.safetyStockQty`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        placeholder={t('products.fields.safetyStockPlaceholder', '미설정')}
                        value={f.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          f.onChange(Number.isFinite(next) ? next : null);
                        }}
                        disabled={trackingOff}
                        aria-label={`${entry.label} ${t('products.fields.safetyStockQty', '안전 재고')}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.weightGrams`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        size="sm"
                        inputMode="numeric"
                        placeholder={t('products.fields.variantWeightGramsPlaceholder', '상품 무게 사용')}
                        value={f.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          f.onChange(Number.isFinite(next) ? next : null);
                        }}
                        aria-label={`${entry.label} ${t('products.fields.variantWeightGrams', '무게 (g)')}`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${idx}.stockTrackingEnabled`}
                render={({ field: f }) => (
                  <FormItem>
                    <FormControl>
                      <label
                        className="inline-flex items-center gap-2 text-[13px]"
                        style={{ color: 'var(--content-default)' }}
                      >
                        <input
                          type="checkbox"
                          checked={f.value ?? true}
                          onChange={(e) => f.onChange(e.target.checked)}
                          aria-label={`${entry.label} ${t('products.fields.stockTrackingEnabled', '재고추적')}`}
                        />
                        {f.value ?? true
                          ? t('products.fields.stockTrackingEnabledOn', '사용 (품절 관리)')
                          : t('products.fields.stockTrackingEnabledOff', '무한판매 (품절 안 됨)')}
                      </label>
                    </FormControl>
                  </FormItem>
                )}
              />
              <span aria-hidden="true" />
            </li>
          );
        })}

        {/* 등록 미리보기 행 — 데카르트 곱 결과 + comboStocks 초기/안전 재고 입력(등록 전용). */}
        {previewCombos.map((pairs, previewIdx) => {
          const key = comboKeyOf(pairs);
          const entry = comboStocks[key];
          const labelText = pairs.map((p) => p.choiceLabel).join(' ');
          return (
            <li
              key={key || previewIdx}
              className="grid grid-cols-1 items-start gap-3 rounded-md p-4 md:items-center"
              style={{
                gridTemplateColumns: SKU_ROW_GRID,
                // §3.4 — 배경 통일(surface-0). dashed border 로 "저장 시 자동 생성(미영속)" 신호.
                background: 'var(--surface-0)',
                border: '1px dashed var(--divider-strong)',
              }}
              data-testid="sku-preview-row"
            >
              <div className={READONLY_CELL_CLASS} style={READONLY_CELL_STYLE}>
                <ComboLabelPairs
                  pairs={pairs}
                  fallback={labelText}
                  testId="sku-preview-label-pairs"
                />
              </div>
              <div
                className={`${READONLY_CELL_CLASS} ${READONLY_VALUE_CLASS}`}
                style={{ ...READONLY_CELL_STYLE, color: 'var(--content-soft)' }}
              >
                {t('products.optionGroups.autoOnSave', { defaultValue: '저장 시 생성' })}
              </div>
              <Input
                size="sm"
                value={entry?.optionCode ?? ''}
                maxLength={OPTION_CODE_MAX}
                onChange={(e) => setComboOptionCode(key, e.target.value)}
                placeholder={t('products.fields.optionCodePlaceholder', '예: A')}
                aria-label={`${labelText} ${t('products.fields.optionCode', '자체 옵션코드')}`}
              />
              <div
                className={`${READONLY_CELL_CLASS} ${READONLY_VALUE_CLASS} text-right`}
                style={{
                  ...READONLY_CELL_STYLE,
                  color: 'var(--content-default)',
                  borderRight: '1px solid var(--divider-soft)',
                }}
              >
                {/* 추가금은 그룹 선택지 추가금 합 — 그룹 편집기 소유라 미리보기에선 — 표기. */}
                {'—'}
              </div>
              <Input
                type="number"
                min={0}
                step="1"
                size="sm"
                inputMode="numeric"
                value={entry?.initialStockQty == null ? '' : entry.initialStockQty}
                onChange={(e) => setComboStock(key, 'initialStockQty', e.target.valueAsNumber)}
                placeholder="0"
                aria-label={`${labelText} ${t('products.fields.initialStockQty', '초기 재고')}`}
              />
              <Input
                type="number"
                min={0}
                step="1"
                size="sm"
                inputMode="numeric"
                value={entry?.safetyStockQty == null ? '' : entry.safetyStockQty}
                onChange={(e) => setComboStock(key, 'safetyStockQty', e.target.valueAsNumber)}
                placeholder={t('products.fields.safetyStockPlaceholder', '미설정')}
                aria-label={`${labelText} ${t('products.fields.safetyStockQty', '안전 재고')}`}
              />
              <span
                className={`${READONLY_VALUE_CLASS} text-right`}
                style={{ color: 'var(--content-soft)' }}
              >
                {'—'}
              </span>
              <span
                className={`${READONLY_VALUE_CLASS} text-center`}
                style={{ color: 'var(--content-soft)' }}
              >
                {/* QA-585 — 등록 시 조합 옵션은 무한판매로 만든다(detailMapper). */}
                {t('products.fields.stockTrackingEnabledOff', '무한판매 (품절 안 됨)')}
              </span>
              <span aria-hidden="true" />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * QA-521 — 옵션이 없는 상품: 기본 SKU 1개의 재고 칸만(현재고·안전재고·무게·재고 추적). 옵션 라벨·옵션 코드는 보이지 않고
 * 라벨은 저장 때 "기본"(detailMapper)으로 채운다.
 */
export const NoOptionStockFields = ({
  form,
  index,
  isEditMode,
}: {
  form: UseFormReturn<ProductFormValues>;
  index: number;
  isEditMode: boolean;
}) => {
  const { t } = useTranslation('products');
  const stockLabel = isEditMode
    ? t('products.fields.currentStockQty', '현재고')
    : t('products.fields.initialStockQty', '초기 재고');
  const trackingOff = form.watch(`variants.${index}.stockTrackingEnabled`) === false;
  const numberField = (
    name: `variants.${number}.initialStockQty` | `variants.${number}.safetyStockQty` | `variants.${number}.weightGrams`,
    label: string,
    opts: { nullable: boolean; placeholder?: string; disabled?: boolean },
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field: f }) => (
        <FormItem>
          <label className="flex flex-col gap-1 text-[13px]" style={{ color: 'var(--content-muted)' }}>
            {label}
            <FormControl>
              <Input
                type="number"
                min={0}
                step="1"
                size="sm"
                inputMode="numeric"
                placeholder={opts.placeholder}
                value={opts.nullable ? (f.value ?? '') : (f.value ?? 0)}
                onChange={(e) => {
                  const next = e.target.valueAsNumber;
                  f.onChange(opts.nullable ? (Number.isFinite(next) ? next : null) : next);
                }}
                disabled={opts.disabled}
                aria-label={label}
              />
            </FormControl>
          </label>
        </FormItem>
      )}
    />
  );
  return (
    <div
      className="grid grid-cols-1 items-end gap-3 rounded-md p-4 md:grid-cols-4"
      style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
      data-testid="no-option-stock-fields"
    >
      {numberField(`variants.${index}.initialStockQty`, stockLabel, { nullable: false, disabled: trackingOff })}
      {numberField(`variants.${index}.safetyStockQty`, t('products.fields.safetyStockQty', '안전 재고'), {
        nullable: true,
        placeholder: t('products.fields.safetyStockPlaceholder', '미설정'),
        disabled: trackingOff,
      })}
      {numberField(`variants.${index}.weightGrams`, t('products.fields.variantWeightGrams', '무게 (g)'), {
        nullable: true,
        placeholder: t('products.fields.variantWeightGramsPlaceholder', '상품 무게 사용'),
      })}
      <FormField
        control={form.control}
        name={`variants.${index}.stockTrackingEnabled`}
        render={({ field: f }) => (
          <FormItem>
            <span className="mb-1 block text-[13px]" style={{ color: 'var(--content-muted)' }}>
              {t('products.fields.stockTrackingEnabled', '재고추적')}
            </span>
            <FormControl>
              <label className="inline-flex h-8 items-center gap-2 text-[13px]" style={{ color: 'var(--content-default)' }}>
                <input
                  type="checkbox"
                  checked={f.value ?? true}
                  onChange={(e) => f.onChange(e.target.checked)}
                  aria-label={t('products.fields.stockTrackingEnabled', '재고추적')}
                />
                {f.value ?? true
                  ? t('products.fields.stockTrackingEnabledOn', '사용 (품절 관리)')
                  : t('products.fields.stockTrackingEnabledOff', '무한판매 (품절 안 됨)')}
              </label>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};
